'use client';

import { useEffect, useRef, useState } from 'react';

const METADATA_TIMEOUT_MS = 10000;
const SEEK_TIMEOUT_MS = 5000;
const JPEG_QUALITY = 0.7;

interface UseThumbnailExtractorProps {
    videoSrc: string | null;
    /** Not the full thumbnail metadata, so a resize cannot re-trigger extraction. */
    times: number[];
}

interface UseThumbnailExtractorReturn {
    /** Blob URL for each thumbnail index, or null if not yet captured. */
    frameUrls: (string | null)[];
    isExtracting: boolean;
    /** True if canvas capture failed (e.g. CORS); caller should fall back to <video>. */
    hasError: boolean;
}

/**
 * Extracts filmstrip thumbnail frames using a single hidden <video> + <canvas>
 * instead of N separate <video> elements. Seeks sequentially and updates state
 * after each frame so thumbnails appear progressively.
 */
export function useThumbnailExtractor({ videoSrc, times }: UseThumbnailExtractorProps): UseThumbnailExtractorReturn {
    const [frameUrls, setFrameUrls] = useState<(string | null)[]>([]);
    const [isExtracting, setIsExtracting] = useState(false);
    const [hasError, setHasError] = useState(false);
    const blobUrlsRef = useRef<string[]>([]);

    useEffect(() => {
        if (!videoSrc || times.length === 0) {
            setFrameUrls((prev) => (prev.length === 0 ? prev : []));
            setIsExtracting(false);
            setHasError(false);
            return;
        }

        let cancelled = false;
        const capturedUrls: string[] = [];

        const extract = async (): Promise<void> => {
            setIsExtracting(true);
            setHasError(false);
            setFrameUrls(new Array(times.length).fill(null));

            const video = document.createElement('video');
            video.crossOrigin = 'anonymous';
            video.muted = true;
            video.preload = 'auto';
            video.src = videoSrc;

            try {
                await new Promise<void>((resolve, reject) => {
                    const timeout = setTimeout(() => reject(new Error('Metadata timeout')), METADATA_TIMEOUT_MS);
                    video.addEventListener(
                        'loadedmetadata',
                        () => {
                            clearTimeout(timeout);
                            resolve();
                        },
                        { once: true },
                    );
                    video.addEventListener(
                        'error',
                        () => {
                            clearTimeout(timeout);
                            reject(new Error('Video load error'));
                        },
                        { once: true },
                    );
                });

                if (cancelled) return;

                const canvas = document.createElement('canvas');
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                const ctx = canvas.getContext('2d');
                if (!ctx) throw new Error('Canvas context unavailable');

                for (let i = 0; i < times.length; i++) {
                    if (cancelled) return;

                    video.currentTime = times[i];

                    await new Promise<void>((resolve, reject) => {
                        const timeout = setTimeout(
                            () => reject(new Error(`Seek timeout at ${times[i]}s`)),
                            SEEK_TIMEOUT_MS,
                        );
                        video.addEventListener(
                            'seeked',
                            () => {
                                clearTimeout(timeout);
                                resolve();
                            },
                            { once: true },
                        );
                    });

                    if (cancelled) return;

                    ctx.drawImage(video, 0, 0);

                    const blob = await new Promise<Blob | null>((resolve) => {
                        canvas.toBlob((b) => resolve(b), 'image/jpeg', JPEG_QUALITY);
                    });

                    if (cancelled || !blob) continue;

                    const url = URL.createObjectURL(blob);
                    capturedUrls.push(url);

                    setFrameUrls((prev) => {
                        const next = [...prev];
                        next[i] = url;
                        return next;
                    });
                }
            } catch (error) {
                if (!cancelled) {
                    console.error('Thumbnail extraction failed:', error);
                    setHasError(true);
                }
            } finally {
                video.src = '';
                video.load();
                if (!cancelled) {
                    setIsExtracting(false);
                    blobUrlsRef.current = capturedUrls;
                }
            }
        };

        void extract();

        return () => {
            cancelled = true;
            blobUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
            blobUrlsRef.current = [];
            capturedUrls.forEach((url) => URL.revokeObjectURL(url));
        };
    }, [videoSrc, times]);

    return { frameUrls, isExtracting, hasError };
}
